import { CalendarClock, Loader2, Repeat } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { adminButton } from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import type { EditScope } from '@/lib/recurrence';

interface EditScopeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChoose: (scope: EditScope) => void;
  /** Disable "this and all future" (e.g. the series has ended). */
  futureDisabled?: boolean;
  busy?: EditScope | null;
}

/** Asked when saving changes to one visit of a repeat clean. */
export function EditScopeDialog({ open, onOpenChange, onChoose, futureDisabled, busy }: EditScopeDialogProps) {
  const option =
    'flex w-full items-start gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="edit-scope-dialog">
        <DialogHeader>
          <DialogTitle>Change a repeat clean</DialogTitle>
          <DialogDescription>This visit is part of a repeat clean. What should the change apply to?</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <button type="button" className={option} onClick={() => onChoose('this')} disabled={Boolean(busy)}>
            <span className="adm-icon-tile h-8 w-8 shrink-0 rounded-[9px] [&_svg]:h-4 [&_svg]:w-4" aria-hidden="true">
              {busy === 'this' ? <Loader2 className="animate-spin" /> : <CalendarClock />}
            </span>
            <span>
              <span className="block text-[13.5px] font-semibold text-foreground">This visit only</span>
              <span className="block text-[12.5px] text-muted-foreground">
                The rest of the repeat clean stays the same.
              </span>
            </span>
          </button>
          <button
            type="button"
            className={option}
            onClick={() => onChoose('future')}
            disabled={Boolean(busy) || futureDisabled}
          >
            <span className="adm-icon-tile h-8 w-8 shrink-0 rounded-[9px] [&_svg]:h-4 [&_svg]:w-4" aria-hidden="true">
              {busy === 'future' ? <Loader2 className="animate-spin" /> : <Repeat />}
            </span>
            <span>
              <span className="block text-[13.5px] font-semibold text-foreground">This and all future visits</span>
              <span className="block text-[12.5px] text-muted-foreground">
                Later visits move to the new pattern. Ones you've already changed, completed or cancelled stay as they are.
              </span>
            </span>
          </button>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className={cn(adminButton.outline)}
            onClick={() => onOpenChange(false)}
            disabled={Boolean(busy)}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
